import React, { useState } from 'react';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompletionTrackingPanel = ({ assignedWorkouts, clients }) => {
  const [timeFilter, setTimeFilter] = useState('today'); // today, week, all

  const getClientName = (clientId) => {
    const client = clients?.find(c => c?.id === clientId);
    return client?.fullName || 'Unknown Client';
  };

  const filterWorkouts = () => {
    const now = new Date();
    const weekFromNow = new Date();
    weekFromNow?.setDate(weekFromNow?.getDate() + 7);

    return assignedWorkouts?.filter(workout => {
      const scheduledDate = new Date(workout?.scheduledDate);
      
      if (timeFilter === 'today') {
        return isToday(scheduledDate);
      } else if (timeFilter === 'week') {
        return scheduledDate >= now && scheduledDate <= weekFromNow;
      }
      return true;
    });
  };

  const filteredWorkouts = filterWorkouts();

  const getStatusBadge = (status, scheduledDate) => {
    const date = new Date(scheduledDate);
    const overdue = isPast(date) && status === 'scheduled';

    const statusConfig = {
      scheduled: {
        bg: overdue ? 'bg-red-500/10' : 'bg-blue-500/10',
        text: overdue ? 'text-red-500' : 'text-blue-500',
        icon: overdue ? 'AlertCircle' : 'Calendar',
        label: overdue ? 'Overdue' : 'Scheduled'
      },
      in_progress: {
        bg: 'bg-yellow-500/10',
        text: 'text-yellow-500',
        icon: 'Clock',
        label: 'In Progress'
      },
      completed: {
        bg: 'bg-green-500/10',
        text: 'text-green-500',
        icon: 'CheckCircle',
        label: 'Completed'
      },
      skipped: {
        bg: 'bg-gray-500/10',
        text: 'text-gray-500',
        icon: 'XCircle',
        label: 'Skipped'
      }
    };

    const config = statusConfig?.[status] || statusConfig?.scheduled;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const getDateLabel = (date) => {
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'MMM dd, yyyy');
  };

  const completionStats = {
    total: filteredWorkouts?.length,
    completed: filteredWorkouts?.filter(w => w?.status === 'completed')?.length,
    inProgress: filteredWorkouts?.filter(w => w?.status === 'in_progress')?.length,
    scheduled: filteredWorkouts?.filter(w => w?.status === 'scheduled')?.length,
    overdue: filteredWorkouts?.filter(w => {
      const date = new Date(w?.scheduledDate);
      return isPast(date) && w?.status === 'scheduled';
    })?.length
  };

  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div>
          <h2 style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 800, fontSize: '1.1rem', color: '#fff', letterSpacing: '-0.01em' }}>
            Workout Completion Tracking
          </h2>
          <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Real-time assignment status and completion monitoring
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant={timeFilter === 'today' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTimeFilter('today')}
          >
            Today
          </Button>
          <Button
            variant={timeFilter === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTimeFilter('week')}
          >
            This Week
          </Button>
          <Button
            variant={timeFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTimeFilter('all')}
          >
            All
          </Button>
        </div>
      </div>
      {/* Completion Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total', value: completionStats?.total, color: 'rgba(255,255,255,0.7)', bg: 'rgba(255,255,255,0.04)' },
          { label: 'Completed', value: completionStats?.completed, color: '#AAFF00', bg: 'rgba(170,255,0,0.07)' },
          { label: 'In Progress', value: completionStats?.inProgress, color: '#FFD700', bg: 'rgba(255,215,0,0.07)' },
          { label: 'Scheduled', value: completionStats?.scheduled, color: '#00D4FF', bg: 'rgba(0,212,255,0.07)' },
          { label: 'Overdue', value: completionStats?.overdue, color: '#ef4444', bg: 'rgba(239,68,68,0.07)' },
        ]?.map(s => (
          <div key={s?.label} style={{ background: s?.bg, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '12px 8px', textAlign: 'center' }}>
            <p style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 800, fontSize: '1.5rem', color: s?.color, lineHeight: 1, letterSpacing: '-0.02em', textShadow: `0 0 12px ${s?.color}44` }}>
              {s?.value}
            </p>
            <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {s?.label}
            </p>
          </div>
        ))}
      </div>
      {/* Workouts List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {filteredWorkouts?.length > 0 ? (
          filteredWorkouts?.map((workout) => (
            <div
              key={workout?.id}
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '12px 16px' }}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>{workout?.workoutPlanName}</h4>
                    {getStatusBadge(workout?.status, workout?.scheduledDate)}
                  </div>
                  <div className="flex items-center gap-4" style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)' }}>
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={13} />
                      {getClientName(workout?.clientId)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={13} />
                      {getDateLabel(new Date(workout?.scheduledDate))}
                    </span>
                  </div>
                </div>
                {workout?.completedAt && (
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
                    Completed: {format(new Date(workout?.completedAt), 'MMM dd, h:mm a')}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Icon name="Calendar" size={40} style={{ color: 'rgba(255,255,255,0.15)', margin: '0 auto 12px' }} />
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem' }}>
              No workouts {timeFilter === 'today' ? 'scheduled for today' : timeFilter === 'week' ? 'this week' : 'found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletionTrackingPanel;